import { useMutation, useQueryClient } from '@tanstack/react-query';

import { importBundle } from '@/lib/api/generated/api';
import type {
  BundleImportResult,
  ImportRejection,
} from '@/lib/api/generated/model';
import { invalidateWorkspace } from '@/features/workspaces';

import { WorkflowRejectedError } from '../errors';
import type { BundleAnswersRequest } from '../upload/bundleAnswers';

type Sent = { file: File; answers: BundleAnswersRequest };

/**
 * A refusal in the service's own words. A workflow the import refused arrives
 * as an `ImportRejection` (every violation, located), anything else as an
 * `Error` with one sentence — a newer format, an unsafe path, a key taken.
 */
export function refusal(status: number, data: unknown): Error {
  if (typeof data === 'object' && data !== null && 'violations' in data) {
    return new WorkflowRejectedError(status, data as ImportRejection);
  }
  if (
    typeof data === 'object' &&
    data !== null &&
    'message' in data &&
    typeof data.message === 'string'
  ) {
    return new Error(data.message);
  }
  return new Error('The bundle could not be read.');
}

/** The answers as the form field carries them; none when there are none. */
export function answersField(
  answers: BundleAnswersRequest,
): string | undefined {
  return Object.keys(answers).length > 0 ? JSON.stringify(answers) : undefined;
}

/** Import every item of a bundle, all or nothing (spec 204). */
export function useImportBundle(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      answers,
    }: Sent): Promise<BundleImportResult> => {
      const response = await importBundle(workspaceId, {
        file,
        answers: answersField(answers),
      });
      if (response.status === 200) return response.data;
      throw refusal(response.status, response.data);
    },
    onSuccess: () => invalidateWorkspace(queryClient, workspaceId),
  });
}
